import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from "@angular/core";

@Component({
	selector: "app-number-selector",
	templateUrl: "./number-selector.component.html",
	styleUrls: ["./number-selector.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberSelectorComponent implements OnChanges {
	@Input() selectedValue: number;
	@Output() selectedValueChange = new EventEmitter<number>();

	// Upper bound for the value. Also drives how many digit columns are shown: the visible column
	// count equals the number of digits in maxValue (e.g. 999 -> 3 columns, 99999 -> 5 columns).
	@Input() maxValue = 99999;

	private readonly allPlaces: DigitPlace[] = ["tenThousands", "thousands", "hundreds", "tens", "single"];
	protected visiblePlaces: DigitPlace[] = this.allPlaces;

	model: NumberSelectorModel = {
		tenThousands: 0,
		thousands: 0,
		hundreds: 0,
		tens: 0,
		single: 0,
	};

	addType(type: DigitPlace) {
		if (this.model[type] < 9) {
			this.model[type]++;
		} else {
			this.model[type] = 0;
		}

		this.setValueFromModel();
	}

	subtractType(type: DigitPlace) {
		if (this.model[type] > 0) {
			this.model[type]--;
		} else {
			this.model[type] = 9;
		}

		this.setValueFromModel();
	}

	add(amount = 1) {
		if (this.selectedValue < this.maxValue) {
			this.setValue(this.selectedValue + amount);
			this.setModelFromValue();
		}
	}

	subtract(amount = 1) {
		if (this.selectedValue > 0) {
			this.setValue(this.selectedValue - amount);
			this.setModelFromValue();
		}
	}

	private setModelFromValue() {
		if (this.selectedValue == null) {
			this.selectedValue = 0;
		}

		const valueString = String(this.selectedValue).padStart(5, "0");

		this.model = {
			tenThousands: parseInt(valueString[0]),
			thousands: parseInt(valueString[1]),
			hundreds: parseInt(valueString[2]),
			tens: parseInt(valueString[3]),
			single: parseInt(valueString[4]),
		};
	}

	private setValueFromModel() {
		const totalString =
			"" +
			this.model.tenThousands +
			this.model.thousands +
			this.model.hundreds +
			this.model.tens +
			this.model.single;

		// Enforce the upper bound even for per-digit input (addType/subtractType cycle a single
		// digit 0-9 and can otherwise compose a value above maxValue). When clamped, resync the
		// digit model so the displayed digits match the clamped value.
		const parsed = parseInt(totalString);
		const clamped = Math.min(parsed, this.maxValue);
		this.setValue(clamped);
		if (clamped !== parsed) {
			this.setModelFromValue();
		}
	}

	private setValue(value: number) {
		this.selectedValue = value;
		this.selectedValueChange.emit(value);
	}

	ngOnChanges(_changes: SimpleChanges): void {
		// If maxValue shrank below the current value (e.g. more seats became unavailable), clamp the
		// bound value down so a stale selection can never exceed the new upper bound.
		if (this.selectedValue != null && this.selectedValue > this.maxValue) {
			this.setValue(this.maxValue);
		}
		this.updateVisiblePlaces();
		this.setModelFromValue();
	}

	private updateVisiblePlaces() {
		const digitCount = Math.min(this.allPlaces.length, Math.max(1, String(Math.max(0, this.maxValue)).length));
		this.visiblePlaces = this.allPlaces.slice(this.allPlaces.length - digitCount);
	}
}

export type DigitPlace = "tenThousands" | "thousands" | "hundreds" | "tens" | "single";

export interface NumberSelectorModel {
	tenThousands: number;
	thousands: number;
	hundreds: number;
	tens: number;
	single: number;
}
